import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { Appointment } from '@/core/appointment/domain/Appointment.ts'
import { Availability, Campaign } from '@/core/availability/domain/Availability.ts'
import { Customer } from '@/core/customer/domain/Customer.ts'
import { PPSOPackage } from '@/core/ppso-package/domain/PPSO-Package.ts'
import { Service } from '@/core/service/domain/Service.ts'
import { Vehicle } from '@/core/vehicle/domain/Vehicle.ts'
import { Workshop } from '@/core/workshop/domain/Workshop.ts'
import { createEmptyVehicle, createEmptyWorkshop } from '@/helpers/constructors.ts'

export const useAppointmentStore = defineStore('appointmentStore', () => {
  const workshop = ref<Workshop>(createEmptyWorkshop())
  const vehicle = ref<Vehicle>(createEmptyVehicle())
  const isVehicleFormValid = ref(false)
  const services = ref<Service[]>([])
  const availability = ref<Record<string, Availability>>({})
  const campaigns = ref<Campaign[]>([])
  const ppsoPackages = ref<PPSOPackage[]>([])
  const date = ref<string>('')
  const availabilityRange = ref<number>(NaN)
  const adviser = ref<{ id: string; name: string; time: string } | undefined>(undefined)
  const isCustomerFormValid = ref(false)
  const customer = ref<Customer>({
    email: '',
    mobilePhone: '',
    name: '',
    phone: ''
  })
  const wasSentRequest = ref(false)
  const enterpriseId = ref('')
  const appointmentId = ref('')
  const appointment = ref<Appointment>({
    adviser: {
      name: ''
    },
    customer: {
      email: '',
      mobilePhone: '',
      name: '',
      phone: ''
    },
    date: '',
    id: '',
    servicesTotalUdts: NaN,
    teamId: NaN,
    time: '',
    vehicle: createEmptyVehicle(),
    workshop: createEmptyWorkshop()
  })

  const reset = () => {
    workshop.value = createEmptyWorkshop()
    vehicle.value = createEmptyVehicle()
    isVehicleFormValid.value = false
    services.value = []
    availability.value = {}
    campaigns.value = []
    ppsoPackages.value = []
    date.value = ''
    availabilityRange.value = NaN
    adviser.value = undefined
    isCustomerFormValid.value = false
    customer.value = {
      email: '',
      mobilePhone: '',
      name: '',
      phone: ''
    }
    wasSentRequest.value = false
    enterpriseId.value = ''
    appointmentId.value = ''
    appointment.value = {
      adviser: {
        name: ''
      },
      customer: {
        email: '',
        mobilePhone: '',
        name: '',
        phone: ''
      },
      date: '',
      id: '',
      servicesTotalUdts: NaN,
      teamId: NaN,
      time: '',
      vehicle: createEmptyVehicle(),
      workshop: createEmptyWorkshop()
    }
  }

  const resetAvailability = () => {
    availability.value = {}
    date.value = ''
    availabilityRange.value = NaN
    adviser.value = undefined
  }

  const availableAdvisers = computed(() => {
    return availability.value[date.value]?.advisers
  })

  const isPPSOPackageNeeded = computed(() => {
    return services.value.some((service) => service.nextServicesAvailable)
  })

  const setServices = (servicesToUpdate: Service[]) => {
    services.value = servicesToUpdate
  }

  return {
    adviser,
    appointment,
    appointmentId,
    availability,
    availabilityRange,
    availableAdvisers,
    campaigns,
    customer,
    date,
    enterpriseId,
    isCustomerFormValid,
    isPPSOPackageNeeded,
    isVehicleFormValid,
    ppsoPackages,
    reset,
    resetAvailability,
    services,
    setServices,
    vehicle,
    wasSentRequest,
    workshop
  }
})
